/**
 * When the office and the studios are open, by weekday (spec §10 · I1).
 *
 * Here rather than in the web app for the reason `rental-terms.ts` and
 * `travel.ts` are here: two things need the same numbers and they live on
 * opposite sides of the repo. The PAGE prints them — a timetable on
 * `/pl/kontakt`, folded runs in every footer, and the start times the studio
 * booker offers — and the SEED publishes them as `offering_availability` rows,
 * which is what the checkout then measures a booking against
 * (`isWithinOpenHours`). A page offering a Saturday the calendar never
 * published is a page that takes a booking the checkout refuses.
 *
 * Owner's figures: *„8-20 od poniedziałku do czwartku, piątek 10-18, sobota
 * 8-22"*. Sunday was not on that list and is shut.
 *
 * MINUTES from midnight, Warsaw wall clock, because that is the unit both
 * consumers need: the page formats them, the seed turns them into instants
 * through `warsawInstant`, and neither has to parse „9:00 – 17:00" to do it.
 */

/** One weekday's opening, or `null` for a day nobody is here. */
export type OfficeOpening = {
  /** `Date.getDay()`: 0 is Sunday. */
  readonly weekday: number
  /** Minutes from midnight, Warsaw. */
  readonly opensAt: number
  readonly closesAt: number
}

const H = (hour: number): number => hour * 60

/**
 * The week, Monday first — which is also the order the contact page prints.
 *
 * A LIST and not a map from weekday to hours, so a day that is shut is simply
 * absent: "no entry" is the only state that cannot be misread as "open at
 * 00:00", and it is what the seed's `find` returns for Sunday.
 */
export const OFFICE_WEEK: readonly OfficeOpening[] = [
  { weekday: 1, opensAt: H(8), closesAt: H(20) },
  { weekday: 2, opensAt: H(8), closesAt: H(20) },
  { weekday: 3, opensAt: H(8), closesAt: H(20) },
  { weekday: 4, opensAt: H(8), closesAt: H(20) },
  { weekday: 5, opensAt: H(10), closesAt: H(18) },
  { weekday: 6, opensAt: H(8), closesAt: H(22) },
]

/** That day's opening, or `null` when the door stays shut. */
export function openingFor(weekday: number): OfficeOpening | null {
  return OFFICE_WEEK.find((day) => day.weekday === weekday) ?? null
}

/** `480` → `"8:00"`. The page's own format, with no leading zero on the hour. */
export function clockLabel(minutes: number): string {
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  return `${String(hour)}:${String(minute).padStart(2, '0')}`
}
